/**
 * Escrow Timeline Engine
 * 
 * Models property tax progression for new construction:
 *   Year 0 (construction): Unimproved land value only
 *   Year 1 (post-completion): Full improved assessed value (may lag)
 *   Year 2+: Homestead + other exemptions applied after filing
 * 
 * Texas-specific exemptions modeled:
 *   - Homestead ($100K general + $100K school district)
 *   - Over-65 / Disabled Person ($10K additional + tax ceiling freeze)
 *   - Surviving Spouse (maintains deceased spouse's ceiling)
 *   - VA Disability (tiered partial or 100% full exemption)
 *   - 10% annual assessed value increase cap (with homestead)
 */

export interface ExemptionProfile {
  homestead: boolean;
  over65: boolean;
  disabledPerson: boolean;
  survivingSpouse: boolean;
  vaDisabilityStatus: 'none' | 'partial' | 'full';
  vaDisabilityRating: number; // 0-100
  veteranAge: number | null;
}

export interface EscrowTimelineInput {
  homePrice: number;
  landValuePercentage: number; // % of home price that is land (typically 20-30%)
  propertyTaxRate: number; // total rate as decimal (e.g., 0.02 for 2%)
  state: string;
  exemptions: ExemptionProfile;
  constructionPeriodMonths: number; // 0 = existing home (no unimproved phase)
  annualAppreciation: number; // e.g., 0.03 for 3%
}

export interface EscrowYearData {
  year: number;
  label: string;
  assessedValue: number;
  taxableValue: number;
  totalExemptions: number;
  exemptionBreakdown: ExemptionBreakdownItem[];
  annualTax: number;
  monthlyEscrow: number;
  annualTaxWithoutExemptions: number;
  monthlyEscrowWithoutExemptions: number;
  effectiveTaxRate: number; // actual tax / assessed value
  cumulativeTaxPaid: number;
  cumulativeSavings: number;
  yoyChange: number; // monthly escrow change from prior year
  yoyChangePct: number;
  phase: 'unimproved' | 'improved-no-exemptions' | 'improved-with-exemptions';
  notes: string[];
  taxCeiling: number | null;
}

export interface ExemptionBreakdownItem {
  name: string;
  amount: number;
  description: string;
  appliesInYear: boolean;
}

export interface EscrowTimelineResult {
  years: EscrowYearData[];
  totalSavingsFromExemptions: number;
  firstYearWithExemptions: number;
  escrowShockWarning: string | null;
  summary: {
    unimprovedMonthly: number;
    improvedNoExemptionsMonthly: number;
    improvedWithExemptionsMonthly: number;
    maxEscrowChange: number;
    maxEscrowChangeYear: number;
  };
}

// ─── Texas VA Disability Tiers ────────────────────────────────
function getTexasVAExemptionAmount(rating: number, homePrice: number): number {
  if (rating >= 100) return homePrice; // Full exemption
  if (rating >= 70) return 12000;
  if (rating >= 50) return 10000;
  if (rating >= 30) return 7500;
  if (rating >= 10) return 5000;
  return 0;
}

// ─── Build exemption breakdown for a given year ───────────────
function buildExemptionBreakdown(
  input: EscrowTimelineInput,
  year: number,
  assessedValue: number,
  isExemptionEligible: boolean
): ExemptionBreakdownItem[] {
  const { exemptions, homePrice } = input;
  const items: ExemptionBreakdownItem[] = [];

  // Homestead: available after filing (year 2+ for new construction, year 1 for existing)
  const homesteadApplies = isExemptionEligible && exemptions.homestead;
  items.push({
    name: 'General Homestead',
    amount: 100000,
    description: 'Texas general homestead exemption - $100,000 off assessed value',
    appliesInYear: homesteadApplies,
  });
  items.push({
    name: 'School District Homestead',
    amount: 100000,
    description: 'School district homestead exemption - $100,000 off school taxes',
    appliesInYear: homesteadApplies,
  });

  // Over-65 or Disabled Person: $10K additional + tax ceiling
  const over65Applies = isExemptionEligible && exemptions.over65;
  items.push({
    name: 'Over-65 Exemption',
    amount: 10000,
    description: 'Additional $10,000 exemption + school tax ceiling freeze',
    appliesInYear: over65Applies,
  });

  const disabledApplies = isExemptionEligible && exemptions.disabledPerson;
  items.push({
    name: 'Disabled Person',
    amount: 10000,
    description: 'Additional $10,000 exemption + school tax ceiling freeze',
    appliesInYear: disabledApplies && !over65Applies, // Can't stack with over-65
  });

  // Surviving Spouse
  const spouseApplies = isExemptionEligible && exemptions.survivingSpouse;
  items.push({
    name: 'Surviving Spouse',
    amount: 0, // Maintains ceiling, not additional dollar amount
    description: 'Maintains deceased spouse\'s tax ceiling on school taxes',
    appliesInYear: spouseApplies,
  });

  // VA Disability
  if (exemptions.vaDisabilityStatus !== 'none') {
    const vaAmount = getTexasVAExemptionAmount(exemptions.vaDisabilityRating, homePrice);
    const vaApplies = isExemptionEligible;
    items.push({
      name: exemptions.vaDisabilityStatus === 'full' ? 'VA 100% Disability' : `VA ${exemptions.vaDisabilityRating}% Disability`,
      amount: vaAmount,
      description: exemptions.vaDisabilityStatus === 'full'
        ? 'Full property tax exemption for 100% disabled veterans'
        : `$${vaAmount.toLocaleString()} exemption for ${exemptions.vaDisabilityRating}% VA disability`,
      appliesInYear: vaApplies,
    });

    // Age 65+ bonus for VA partial disability
    if (exemptions.vaDisabilityStatus === 'partial' && exemptions.veteranAge && exemptions.veteranAge >= 65) {
      items.push({
        name: 'VA 65+ Additional',
        amount: 12000,
        description: 'Additional $12,000 for veterans 65+ with 10%+ disability',
        appliesInYear: vaApplies,
      });
    }
  }

  return items;
}

// ─── Calculate total exemption amount for a year ──────────────
function calculateTotalExemptions(breakdown: ExemptionBreakdownItem[], assessedValue: number): number {
  const total = breakdown
    .filter(item => item.appliesInYear && item.amount > 0)
    .reduce((sum, item) => sum + item.amount, 0);
  return Math.min(total, assessedValue);
}

// ─── Main timeline generator ─────────────────────────────────
export function generateEscrowTimeline(input: EscrowTimelineInput): EscrowTimelineResult {
  const {
    homePrice,
    landValuePercentage,
    propertyTaxRate,
    exemptions,
    constructionPeriodMonths,
    annualAppreciation,
  } = input;

  const rate = propertyTaxRate < 1 ? propertyTaxRate : propertyTaxRate / 100;
  const landValue = homePrice * (landValuePercentage / 100);
  const isNewConstruction = constructionPeriodMonths > 0;
  const yearsToModel = 10;
  const years: EscrowYearData[] = [];

  let taxCeiling: number | null = null;

  for (let y = 0; y < yearsToModel; y++) {
    const appreciationMultiplier = Math.pow(1 + annualAppreciation, y);
    const notes: string[] = [];

    let phase: EscrowYearData['phase'];
    let assessedValue: number;
    let isExemptionEligible: boolean;

    if (isNewConstruction && y === 0) {
      // Year 0: Under construction - only land value taxed
      phase = 'unimproved';
      assessedValue = landValue;
      isExemptionEligible = false;
      notes.push('Under construction — only land value assessed');
    } else if (isNewConstruction && y === 1) {
      // Year 1: Completed, full assessed value, but homestead not yet filed
      phase = 'improved-no-exemptions';
      assessedValue = homePrice * appreciationMultiplier;
      isExemptionEligible = false;
      notes.push('Home completed — full assessed value, exemptions not yet filed');
      notes.push('⚠️ Escrow will increase significantly this year');
    } else {
      // Year 2+: Exemptions applied
      phase = 'improved-with-exemptions';
      assessedValue = homePrice * appreciationMultiplier;

      // Apply 10% cap on assessed value increase (with homestead)
      if (exemptions.homestead && y >= 2) {
        const prevAssessed = homePrice * Math.pow(1 + annualAppreciation, y - 1);
        const maxIncrease = prevAssessed * 1.10;
        if (assessedValue > maxIncrease) {
          assessedValue = maxIncrease;
          notes.push('10% homestead cap applied to assessed value increase');
        }
      }

      isExemptionEligible = true;
      if (y === 2 && isNewConstruction) {
        notes.push('Homestead exemption now in effect');
      }
    }

    // For existing homes (not new construction), exemptions apply from year 1
    if (!isNewConstruction) {
      if (y === 0) {
        phase = 'improved-no-exemptions';
        isExemptionEligible = false;
        notes.push('First year — exemptions may not be in effect yet');
      } else {
        phase = 'improved-with-exemptions';
        isExemptionEligible = true;
      }
      assessedValue = homePrice * appreciationMultiplier;
    }

    const breakdown = buildExemptionBreakdown(input, y, assessedValue, isExemptionEligible);
    const totalExemptions = calculateTotalExemptions(breakdown, assessedValue);
    const taxableValue = Math.max(0, assessedValue - totalExemptions);
    const annualTax = taxableValue * rate;
    const monthlyEscrow = annualTax / 12;

    // Tax ceiling logic for over-65 / disabled
    if (isExemptionEligible && (exemptions.over65 || exemptions.disabledPerson)) {
      if (taxCeiling === null) {
        taxCeiling = annualTax;
        notes.push('Tax ceiling established (school taxes frozen at this level)');
      } else if (annualTax > taxCeiling) {
        notes.push(`Tax ceiling applied — school taxes capped at $${Math.round(taxCeiling).toLocaleString()}/yr`);
      }
    }

    const label = isNewConstruction
      ? y === 0 ? 'Construction' : `Year ${y}`
      : `Year ${y + 1}`;

    const annualTaxWithoutExemptions = assessedValue * rate;
    const prevMonthly = years.length > 0 ? years[years.length - 1].monthlyEscrow : 0;
    const prevCumulativeTax = years.length > 0 ? years[years.length - 1].cumulativeTaxPaid : 0;
    const prevCumulativeSavings = years.length > 0 ? years[years.length - 1].cumulativeSavings : 0;
    const yoyChange = years.length > 0 ? Math.round(monthlyEscrow) - prevMonthly : 0;
    const yoyChangePct = prevMonthly > 0 ? ((Math.round(monthlyEscrow) - prevMonthly) / prevMonthly) * 100 : 0;
    const effectiveTaxRate = assessedValue > 0 ? annualTax / assessedValue : 0;
    const yearSavings = annualTaxWithoutExemptions - annualTax;

    years.push({
      year: y,
      label,
      assessedValue: Math.round(assessedValue),
      taxableValue: Math.round(taxableValue),
      totalExemptions: Math.round(totalExemptions),
      exemptionBreakdown: breakdown,
      annualTax: Math.round(annualTax),
      monthlyEscrow: Math.round(monthlyEscrow),
      annualTaxWithoutExemptions: Math.round(annualTaxWithoutExemptions),
      monthlyEscrowWithoutExemptions: Math.round(annualTaxWithoutExemptions / 12),
      effectiveTaxRate: parseFloat((effectiveTaxRate * 100).toFixed(3)),
      cumulativeTaxPaid: Math.round(prevCumulativeTax + annualTax),
      cumulativeSavings: Math.round(prevCumulativeSavings + yearSavings),
      yoyChange,
      yoyChangePct: parseFloat(yoyChangePct.toFixed(1)),
      phase,
      notes,
      taxCeiling: taxCeiling ? Math.round(taxCeiling) : null,
    });
  }

  // Summary calculations
  const unimprovedYear = years.find(y => y.phase === 'unimproved');
  const noExemptYear = years.find(y => y.phase === 'improved-no-exemptions');
  const withExemptYear = years.find(y => y.phase === 'improved-with-exemptions');
  const firstExemptionYear = years.findIndex(y => y.phase === 'improved-with-exemptions');

  // Total savings from exemptions over modeled period
  const totalSavings = years.reduce((sum, y) => sum + (y.totalExemptions * rate), 0);

  // Escrow shock detection
  let maxEscrowChange = 0;
  let maxEscrowChangeYear = 0;
  for (let i = 1; i < years.length; i++) {
    const change = years[i].monthlyEscrow - years[i - 1].monthlyEscrow;
    if (Math.abs(change) > Math.abs(maxEscrowChange)) {
      maxEscrowChange = change;
      maxEscrowChangeYear = i;
    }
  }

  const escrowShockWarning = maxEscrowChange > 200
    ? `⚠️ Your escrow may jump ~$${Math.round(maxEscrowChange)}/mo in ${years[maxEscrowChangeYear]?.label} when the full assessed value takes effect. Budget accordingly.`
    : null;

  return {
    years,
    totalSavingsFromExemptions: Math.round(totalSavings),
    firstYearWithExemptions: firstExemptionYear >= 0 ? firstExemptionYear : 2,
    escrowShockWarning,
    summary: {
      unimprovedMonthly: unimprovedYear?.monthlyEscrow ?? 0,
      improvedNoExemptionsMonthly: noExemptYear?.monthlyEscrow ?? 0,
      improvedWithExemptionsMonthly: withExemptYear?.monthlyEscrow ?? 0,
      maxEscrowChange: Math.round(maxEscrowChange),
      maxEscrowChangeYear,
    },
  };
}
